// Editing one caption, with the two things that are hard to get right about it
// visible at the same time: what it says, and what it will actually say.
//
// The preview is not decoration. A caption reading "Book at {{booking}}" is
// correct and unreadable; the same caption resolved is readable and is what
// goes out. Showing only the first invites publishing a caption whose tokens
// were never going to resolve, which is the one failure this whole feature can
// produce and the one it must therefore make impossible to miss.
//
// "Save as a copy" is the primary action when the original was not written
// here — a library filled from a spreadsheet is a set of starting points, and
// the common move is to take one and make this week's version of it, not to
// overwrite the thing you imported.

import { useEffect, useMemo, useRef, useState } from "react";
import { blanksIn, resolveTokens, tokensIn } from "../lib/tokens";
import { MAX_CAPTION_CHARS } from "../lib/sheet";
import { Spinner } from "./Spinner";
import type { BrandDetail, ContentDraft } from "../lib/types";

export function CaptionEditor({
  draft, details, busy, onSave, onClose,
}: {
  /** Null when writing a new one from nothing. */
  draft: ContentDraft | null;
  details: BrandDetail[];
  busy: boolean;
  onSave: (body: string, asCopy: boolean) => void | Promise<void>;
  onClose: () => void;
}) {
  const [body, setBody] = useState(draft?.body ?? "");
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const values = useMemo(
    () => new Map(details.map((d) => [d.key, d.value])),
    [details],
  );
  const resolved = useMemo(() => resolveTokens(body, values), [body, values]);
  const blanks = useMemo(() => blanksIn(body), [body]);
  const unknown = resolved.missing;

  /** At the cursor, not at the end — the whole point is filling a gap mid-sentence. */
  function insert(key: string) {
    const el = box.current;
    const token = `{{${key}}}`;
    if (!el) { setBody(body + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? start;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // After React has written the new value, or the caret jumps to the end.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  const tooLong = body.length > MAX_CAPTION_CHARS;
  const empty = !body.trim();
  const changed = body !== (draft?.body ?? "");

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal wide" role="dialog" aria-modal="true"
           aria-labelledby="caption-editor-title" onClick={(e) => e.stopPropagation()}>
        <div className="storage-head">
          <h2 id="caption-editor-title">{draft ? "Edit caption" : "New caption"}</h2>
        </div>

        <label htmlFor="caption-body">The caption</label>
        <textarea id="caption-body" ref={box} rows={9} value={body}
                  onChange={(e) => setBody(e.target.value)} />

        <div className="row editor-meta">
          <span className={tooLong ? "err-text" : "muted"}>
            {body.length.toLocaleString()} / {MAX_CAPTION_CHARS.toLocaleString()} characters
          </span>
        </div>

        {details.length > 0 && (
          <>
            <p className="muted editor-insert-head">Drop in a brand detail:</p>
            <div className="row editor-tokens">
              {details.map((d) => (
                <button key={d.id} type="button" className="chip"
                        title={d.value} onClick={() => insert(d.key)}>
                  {d.label}
                </button>
              ))}
            </div>
          </>
        )}

        <h3 className="editor-preview-head">What it will say</h3>
        <div className="editor-preview">
          {resolved.text.trim() || <span className="muted">Nothing yet.</span>}
        </div>

        {(unknown.length > 0 || blanks.length > 0) && (
          <ul className="parse-tally editor-warnings">
            {unknown.length > 0 && (
              <li className="warn-item">
                no brand detail called {unknown.map((k) => `{{${k}}}`).join(", ")} — it will
                publish exactly as written
              </li>
            )}
            {blanks.length > 0 && (
              <li className="warn-item">
                {blanks.length} still to write: {blanks.slice(0, 3).map((b) => `[${b}]`).join(", ")}
                {blanks.length > 3 ? ", …" : ""}
              </li>
            )}
          </ul>
        )}

        <div className="row modal-actions">
          {draft && (
            <button className="primary" disabled={busy || empty || tooLong || !changed}
                    onClick={() => onSave(body, false)}>
              {busy && <Spinner />} Save changes
            </button>
          )}
          <button className={draft ? undefined : "primary"} disabled={busy || empty || tooLong}
                  onClick={() => onSave(body, true)}>
            {busy && !draft && <Spinner />} {draft ? "Save as a copy" : "Save"}
          </button>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          {tooLong && (
            <span className="err-text">
              Too long by {(body.length - MAX_CAPTION_CHARS).toLocaleString()} characters.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** So a caller can show the same summary in a table row without re-parsing. */
export function tokenSummary(body: string): { tokens: string[]; blanks: number } {
  return { tokens: tokensIn(body), blanks: blanksIn(body).length };
}
